import clsx from 'clsx'
import React, {useEffect, useState} from 'react'
import {Overlay, OverlayTrigger} from 'react-bootstrap'
import {useNavigate, useSearchParams} from 'react-router-dom'
import useAppToast from 'src/hooks/useAppToast'
import {fetchUserAccountDetailsAsync} from 'src/redux/actions/accountActions'
import {getURL, setPlatformChoiceAsync} from 'src/redux/actions/authActions'
import {fetchConnectedPlatformAsync} from 'src/redux/actions/platformActions'
import {setStepsData} from 'src/redux/slices/tourPopupSlice'
import {useAppDispatch, useAppSelector} from 'src/redux/storeHooks'
import {Box, Divider, Tooltip, Typography} from '@mui/material'
import {BaseNames} from 'src/marketConnect/helpers'

const ConfirmPlatformConnectionType = () => {
  const [channelNature, setChannelNature] = useState<any>('PRIMARY')
  const [selectedNature, setSelectedNature] = useState<any>('')
  const dispatch = useAppDispatch()
  const [friendlyName, setFriendlyName] = useState('')
  const [loading, setLoading] = useState(false)
  const {successToast, warningToast} = useAppToast()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const previewSync = searchParams.get('preview_sync') === 'true'
  const previewOnboard = searchParams.get('preview_onboard') === 'true'
  const [loader, setLoader] = useState(false)
  const [url, setURL] = useState<string>('')
  const {stepsData} = useAppSelector((state) => state.tourPopup)
  const {fetchedConnectedPlatformData, fetchedConnectedPlatformLoading, mainStoreData} =
    useAppSelector((state) => state.fetchConnectedPlatform)
  const {fetchedUserAccountData} = useAppSelector((state) => state.userAccountData)
  const {initialSyncProgressDetail} = useAppSelector((state) => state.auth)
  const primaryPlatform = fetchedConnectedPlatformData?.[0] || mainStoreData
  const platformCode = (primaryPlatform?.code || '').toString().trim().toUpperCase()
  const accountSource = (fetchedUserAccountData?.source || '').toString().trim().toUpperCase()
  const platformLabel =
    primaryPlatform?.display_name ||
    primaryPlatform?.platform_info?.display_name ||
    primaryPlatform?.platform_info?.name
  const platformDisplayName = platformLabel || 'Store'
  const platformStoreNameRaw = platformLabel || accountSource || 'store'
  const platformStoreName = platformLabel
    ? `${platformLabel} store`
    : accountSource
    ? `${accountSource} store`
    : 'store'
  const isNameAutoMode = accountSource === 'DISCONNECT' || accountSource === 'BIGCOMMERCE'
  const shouldShowDiscogsCta = accountSource === 'DISCONNECT'
  const isFirstTimeOnboard =
    fetchedUserAccountData?.isOnboardingProcessCompleted === false ||
    fetchedUserAccountData?.is_platform_choice_done === false
  // Rich layout (congrats, centered logo, sync stats) is for DISCONNECT registration only.
  // preview_* params remain for QA on any source.
  const showPreviewLayout =
    previewSync ||
    previewOnboard ||
    (isFirstTimeOnboard && accountSource === 'DISCONNECT')
  const getUserInputStoreName = (platform: any) =>
    platform?.friendlyName ||
    platform?.friendly_name ||
    platform?.store_name ||
    platform?.credentials?.identifier ||
    platform?.display_name ||
    platform?.platform_info?.display_name ||
    platform?.platform_info?.name ||
    '-'
  const disconnectedMessage = ['WIX', 'ECWID', 'BIGCOMMERCE'].includes(accountSource)
    ? `Oops! You have uninstalled the Commercium by ConstaCloud App from your ${platformStoreNameRaw}. Please reinstall it again to get started.`
    : `Your ${BaseNames.PRIMARY_Name} Channel has been disconnected. To reconnect, please go to your ${platformStoreName} and install our 'Commercium by ConstaCloud' app`

  useEffect(() => {
    const nature = mainStoreData?.platform_info?.nature
    if (nature) {
      setChannelNature(nature)
      setSelectedNature(nature === 'BOTH' ? 'PRIMARY' : nature)
    }
  }, [mainStoreData])

  // condition for when source is disconnect count platform channel connect and set name with incrementing one
  useEffect(() => {
    if (
      !fetchedConnectedPlatformLoading &&
      fetchedUserAccountData?.source &&
      (fetchedUserAccountData?.source === 'DISCONNECT' ||
        fetchedUserAccountData?.source === 'BIGCOMMERCE') &&
      platformCode
    ) {
      const platformCount =
        fetchedConnectedPlatformData?.filter((platform) => platform.code === platformCode).length ??
        0
      if (platformCount >= 1) {
        setFriendlyName(`${platformDisplayName} Channel ${platformCount + 1}`)
      } else setFriendlyName(`${platformDisplayName} Channel 1`)
    }
  }, [
    fetchedConnectedPlatformLoading,
    fetchedUserAccountData?.source,
    fetchedConnectedPlatformData,
    platformCode,
    platformDisplayName,
  ])

  const handleChangePlatformConnectionType = async () => {
    if (fetchedUserAccountData) {
      const finalFriendlyName =
        friendlyName || (isNameAutoMode ? `${platformDisplayName} Channel 1` : '')
      if (!finalFriendlyName) {
        warningToast('Channel name is required field')
        return
      }
      if (!friendlyName) {
        setFriendlyName(finalFriendlyName)
      }

      const payload = {
        isMainStore: selectedNature == 'PRIMARY' ? true : false,
        user_id: fetchedUserAccountData?.id!,
        platform_id: mainStoreData?.id,
        friendlyName: finalFriendlyName,
      }
      console.log(payload, 'pay')
      setLoading(true)
      dispatch(setPlatformChoiceAsync(payload))
        .then((res) => {
          console.log('response of platform choice', res.payload)
          if (res.payload?.status_code == 200) {
            successToast('Channel connected successfully')
            const params = new URLSearchParams(window.location.search)
            if (params.has('preview_onboard')) {
              params.delete('preview_onboard')
              navigate(
                {
                  pathname: window.location.pathname,
                  search: params.toString() ? `?${params.toString()}` : '',
                },
                {replace: true}
              )
            }
            dispatch(fetchUserAccountDetailsAsync())
            dispatch(fetchConnectedPlatformAsync()).then(() => {
              dispatch(
                setStepsData({
                  stepData: {stepOne: 'finished', stepTwo: 'active', stepThree: 'inactive'},
                  visible: true,
                })
              )
            })
            // Persist locally to avoid re-showing confirmation on refresh before account flag updates
            if (fetchedUserAccountData?.id) {
              localStorage.setItem(`platform_choice_done_${fetchedUserAccountData.id}`, 'true')
            }
          }
          setLoading(false)
        })
        .catch((err) => {
          setLoading(false)
          warningToast('Connection type not changed' + err + '')
        })
    }
  }

  const getLink = () => {
    setLoader(true)
    dispatch(getURL())
      .then((response) => {
        const redirectUrl = response?.payload?.data?.data?.redirect_url
        if (response?.payload?.data?.status_code === 201 && redirectUrl) {
          window.open(redirectUrl, '_blank')
        }
      })
      .finally(() => setLoader(false))
  }

  return (
    <div className='d-flex flex-column'>
      {fetchedConnectedPlatformData?.length ? (
        <>
          <div className='d-flex justify-content-between align-items-center'>
            {fetchedUserAccountData?.source === 'DISCONNECT' ||
            fetchedUserAccountData?.source === 'BIGCOMMERCE' ? null : (
              <h5>Confirm Source Channel Connection</h5>
            )}
            {fetchedUserAccountData?.source === 'DISCONNECT' ||
            fetchedUserAccountData?.source === 'BIGCOMMERCE' ? null : (
              <button
                type='button'
                className='btn btn-sm btn-primary fs-6 fw-bold'
                onClick={() => {
                  dispatch(setStepsData({stepData: stepsData, visible: false}))
                  navigate('/pricing')
                }}
              >
                {' '}
                <i className={'bi bi-coin fs-4 me-2'}></i> Manage Plan
              </button>
            )}
          </div>
          {showPreviewLayout && isFirstTimeOnboard && (
            <div className='text-center mb-2'>
              <p className='fs-4 fw-bold text-success'>
                <i className='bi bi-check-circle-fill me-2 text-success'></i>
                Congratulations! Your {platformDisplayName} store is successfully connected.
              </p>
            </div>
          )}
          <div
            className={clsx(
              'row align-items-center justify-content-center',
              showPreviewLayout && isFirstTimeOnboard ? 'mt-2 mb-4' : 'my-5'
            )}
          >
            <div className='col-xl-7 col-lg-9 col-md-10 col-12'>
              <div className='card min-h-100'>
                <div className={showPreviewLayout ? 'card-body p-4' : 'card-body p-5'}>
                  {showPreviewLayout ? (
                    <>
                      <div className='d-flex flex-column align-items-center pb-4'>
                        <img
                          src={fetchedConnectedPlatformData[0]?.platform_info?.logo_ref}
                          alt={fetchedConnectedPlatformData[0]?.display_name}
                          style={{height: 64, objectFit: 'contain'}}
                          className='mb-3'
                        />
                        <p className='fs-3 fw-bold mb-0'>
                          {fetchedConnectedPlatformData[0]?.display_name}
                        </p>
                      </div>

                      <div className='separator mb-4'></div>

                      {(() => {
                        const platform = fetchedConnectedPlatformData[0]
                        const productsDetail = initialSyncProgressDetail.find(
                          (d) => d.display_name === 'Products'
                        )
                        const locationsDetail = initialSyncProgressDetail.find(
                          (d) => d.display_name === 'Location'
                        )
                        const totalProducts =
                          productsDetail != null
                            ? productsDetail.total_qty_available_in_channel
                            : null
                        const totalLocations =
                          locationsDetail != null
                            ? locationsDetail.total_qty_available_in_channel
                            : null

                        return (
                          <div className='row g-3 mb-4'>
                            <div className='col-6'>
                              <span className='text-dark'>Store Name : </span>
                              <span className='fw-bold'>{getUserInputStoreName(platform)}</span>
                            </div>
                            <div className='col-6'>
                              <span className='text-dark'>Store Currency : </span>
                              <span className='fw-bold'>
                                {(platform as any)?.currency_iso ||
                                  (platform as any)?.credentials?.currency ||
                                  '-'}
                              </span>
                            </div>
                            <div className='col-6'>
                              <span className='text-dark'>Total Products : </span>
                              <span className='fw-bold'>
                                {totalProducts != null ? totalProducts.toLocaleString() : '-'}
                              </span>
                            </div>
                            <div className='col-6'>
                              <span className='text-dark'>Total Locations : </span>
                              <span className='fw-bold'>
                                {totalLocations != null ? totalLocations.toLocaleString() : '-'}
                              </span>
                            </div>
                          </div>
                        )
                      })()}

                      <div className='separator mb-4'></div>
                    </>
                  ) : (
                    <>
                      <div className='row align-items-center'>
                        <div className='col-8'>
                          <p className='fs-4 fw-semibold'>
                            {fetchedConnectedPlatformData[0]?.display_name}
                          </p>
                        </div>
                        <div className='col-4 text-end'>
                          <img
                            src={fetchedConnectedPlatformData[0]?.platform_info?.logo_ref}
                            className='h-50px'
                            alt={fetchedConnectedPlatformData[0]?.display_name}
                          />
                        </div>
                      </div>
                      <div className='separator my-2'></div>
                    </>
                  )}
                  <div className='mt-4'>
                    {fetchedUserAccountData?.source === 'DISCONNECT' ||
                    fetchedUserAccountData?.source === 'BIGCOMMERCE' ? (
                      <label className='form-label' htmlFor=''>
                        {showPreviewLayout
                          ? `Now, please select if you want your ${platformStoreName} to act as a Source or Destination channel ?`
                          : `Your ${platformStoreName} is successfully connected. Now, please select if you want your ${platformStoreName} to act as a Source or Destination channel ?`}
                      </label>
                    ) : (
                      <>
                        {' '}
                        <label className='form-label' htmlFor=''>
                          Channel Name
                        </label>
                        <input
                          type='text'
                          required
                          value={friendlyName}
                          onChange={(ev) => setFriendlyName(ev.target.value)}
                          placeholder='Add a Channel Name to remember this Store connection'
                          className='form-control form-control-solid'
                        />
                      </>
                    )}{' '}
                  </div>
                  <div className='row mt-8 d-flex justify-content-center'>
                    {/* Primary Channel */}
                    <div className='col-sm-6 col-12 mb-4 mb-sm-0'>
                      <label
                        htmlFor='primaryConnection'
                        className={`border p-3 py-3 rounded shadow h-100 d-flex align-items-center w-100
                        bg-hover-light-primary 
                        ${
                          channelNature !== 'PRIMARY' && channelNature !== 'BOTH'
                            ? 'opacity-50 cursor-not-allowed'
                            : ''
                        }
                      `}
                      >
                        <input
                          id='primaryConnection'
                          value='PRIMARY'
                          checked={selectedNature === 'PRIMARY'}
                          type='radio'
                          className='form-check-input mt-0'
                          disabled={channelNature !== 'PRIMARY' && channelNature !== 'BOTH'}
                          onChange={(ev) => setSelectedNature(ev.target.value)}
                        />
                        <p
                          className={clsx(
                            'ms-3 mb-0 d-flex align-items-center',
                            selectedNature === 'PRIMARY' ? 'text-primary fw-bold fs-5' : 'fs-5',
                            channelNature !== 'PRIMARY' && channelNature !== 'BOTH'
                              ? 'text-muted'
                              : 'text-dark'
                          )}
                        >
                          Make it a Source Channel
                        </p>
                      </label>
                      <div className='d-flex align-items-center mt-2 ms-2'>
                        <Tooltip
                          placement='top'
                          arrow
                          componentsProps={{
                            tooltip: {
                              sx: {
                                backgroundColor: '#333',
                                color: '#fff',
                                fontSize: '14px',
                                maxWidth: '43rem',
                                maxHeight: '14rem',
                                position: 'relative',
                                left: '5.5rem',
                                overflowY: 'auto',
                                bottom: '-12px',
                              },
                            },
                            arrow: {
                              sx: {
                                color: '#333',
                              },
                            },
                          }}
                          title={
                            <Box>
                              <Typography component='span'>
                                * If you add this store as a Source platform then
                                <br />
                                You will be able to send below data from this store to other
                                platforms that you connect here
                                <br />- Products
                                <span style={{marginLeft: '9rem'}}>- Inventories</span>
                                <br />- Price Updates
                                <span style={{marginLeft: '6.5rem'}}>- Shipments</span>
                                <br />
                                &amp; you will be able to receive below data from other platforms to
                                this Source platform that you connect here
                                <br />- Orders
                                <span style={{marginLeft: '9.5rem'}}>-Customers </span>
                                <br />- Payments
                                <span style={{marginLeft: '8.1rem'}}>-Invoices</span>
                                <br />- Returns/Refunds
                                <span style={{marginLeft: '5rem'}}>-Cancellations</span>
                                <br />
                                -Marketplace Fund Settlements
                                <br />
                                <Divider sx={{borderColor: '#f8f8f8ff', borderBottomWidth: 1}} />
                                Note : This is recommended option if you are looking to connect your
                                store with a marketplace. If still not sure reach us out at{' '}
                                <span style={{color: '#3e97ff'}}>help@mycommercium.com</span>
                              </Typography>
                            </Box>
                          }
                        >
                          <Box
                            className='d-flex align-items-center gap-2 mt-2 ms-1'
                            sx={{cursor: 'pointer'}}
                          >
                            <Typography variant='body2' color='text.secondary' sx={{mt: 0.3}}>
                              What is {BaseNames.PRIMARY_Name} Channel?
                            </Typography>
                            <i className='bi bi-info-circle fs-5 text-secondary' />
                          </Box>
                        </Tooltip>
                      </div>
                    </div>

                    {/* Secondary Channel */}
                    <div className='col-sm-6 col-12'>
                      <label
                        htmlFor='secondaryConnection'
                        className={`border p-3 py-3 rounded shadow h-100 d-flex align-items-center w-100
                        bg-hover-light-primary 
                        ${
                          channelNature !== 'SECONDARY' && channelNature !== 'BOTH'
                            ? 'opacity-50 cursor-not-allowed'
                            : ''
                        }
                      `}
                      >
                        <input
                          id='secondaryConnection'
                          value='SECONDARY'
                          checked={selectedNature === 'SECONDARY'}
                          type='radio'
                          className='form-check-input mt-0'
                          disabled={channelNature !== 'SECONDARY' && channelNature !== 'BOTH'}
                          onChange={(ev) => setSelectedNature(ev.target.value)}
                        />
                        <p
                          className={clsx(
                            'ms-3 mb-0 d-flex align-items-center',
                            selectedNature === 'SECONDARY' ? 'text-primary fw-bold fs-5' : 'fs-5',
                            channelNature !== 'SECONDARY' && channelNature !== 'BOTH'
                              ? 'text-muted'
                              : 'text-dark'
                          )}
                        >
                          Make it a Destination Channel
                        </p>
                      </label>
                      <div className='d-flex align-items-center mt-2 ms-2'>
                        <Tooltip
                          placement='top'
                          arrow
                          componentsProps={{
                            tooltip: {
                              sx: {
                                backgroundColor: '#333',
                                color: '#fff',
                                fontSize: '14px',
                                maxWidth: '43rem',
                                maxHeight: '14rem',
                                position: 'relative',
                                left: '5.2rem',
                                overflowY: 'auto',
                                bottom: '-12px',
                              },
                            },
                            arrow: {
                              sx: {
                                color: '#333',
                              },
                            },
                          }}
                          title={
                            <Box>
                              <Typography component='span' display='block'>
                                If you add this store as a Destination platform then
                                <br />
                                You will be able to send below data from this platform to other
                                connected Source platform that you connect here
                                <br />- Orders
                                <span style={{marginLeft: '13.4rem'}}>- Customers</span>
                                <br />- Payments
                                <span style={{marginLeft: '12rem'}}>- Invoices</span>
                                <br />- Payout Settlements
                                <span style={{marginLeft: '7.7rem'}}>- Cancellations</span>
                                <br />
                                - Returns/Refunds
                                <br />
                                You will be able to receive below data from other platform to this
                                Destination platform
                                <br />- Products
                                <span style={{marginLeft: '12.6rem'}}>- Inventories</span>
                                <br />- Price Updates
                                <span style={{marginLeft: '10.3rem'}}>- Shipments</span>
                                <br />
                                Note : This is recommended option if you are looking to connect your
                                store with a ERP, OMS or WMS. If still not sure reach us out at{' '}
                                <span style={{color: '#3e97ff'}}>help@mycommercium.com</span>
                              </Typography>
                            </Box>
                          }
                        >
                          <Box
                            className='d-flex align-items-center gap-2 mt-2 ms-1'
                            sx={{cursor: 'pointer'}}
                          >
                            <Typography variant='body2' color='text.secondary' sx={{mt: 0.2}}>
                              What is {BaseNames.SECONDARY_Name} Channel?
                            </Typography>
                            <i className='bi bi-info-circle fs-5 text-secondary'></i>
                          </Box>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </div>

                <div className='d-flex justify-content-center px-5 pt-7 pb-0 text-center'>
                  {shouldShowDiscogsCta && (
                    <p className='fs-5 mb-0 fw-bold text-dark'>
                      Want to connect to Discogs marketplace instead ?{' '}
                      {loader ? (
                        <span
                          style={{color: '#999', cursor: 'not-allowed', textDecoration: 'none'}}
                        >
                          <u>Click Here</u>
                        </span>
                      ) : (
                        <a
                          // href={url}
                          onClick={getLink}
                          target='_blank'
                          rel='noopener noreferrer'
                          style={{
                            textDecoration: 'underline',
                            color: '#0d6efd',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                          }}
                        >
                          Click Here
                        </a>
                      )}
                    </p>
                  )}
                </div>
                <div className='d-flex align-items-center justify-content-center px-5 pt-8 pb-5'>
                  <button
                    disabled={loading}
                    className='btn btn-primary btn-sm w-50'
                    onClick={handleChangePlatformConnectionType}
                  >
                    {loading ? 'Saving...' : 'Save & Proceed'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className='p-5 mt-5'>
          <div className='card'>
            <div className='card-body'>
              <h5>{disconnectedMessage}</h5>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ConfirmPlatformConnectionType
